import {
  Box,
  Flex,
  Button,
  Text,
  Drawer,
  Checkbox,
  UnstyledButton,
  HoverCard,
  Pagination,
  TextInput
} from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import Table from '../../components/Table';
import moment from 'moment';
import PageHeader from '../../components/PageHeader';
import { CloseButton } from '@mantine/core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useDeleteWorkflow,
  useDeleteWorkflowGroup,
  useGetWorkflows
} from '../../services/workflow.services';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useUserCompanies,
  useUserCompaniesCurrentUserAdminState,
  useUserGroupsAllUsers
} from '../../services/user.service';
import { checkToRedirect } from '../../utils/checkToRedirect';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { IoIosArrowRoundBack } from 'react-icons/io';
import AssignedToColumn, {
  ASSIGNEE_EDITING_STATES
} from './components/ProjectTable/AssignedToColumn';
import authStore from '../../store/auth/index';
import {
  TbCopy,
  TbExternalLink,
  TbListSearch,
  TbPlus,
  TbSearch,
  TbSitemap,
  TbTrash,
  TbUser
} from 'react-icons/tb';
import { FilterDropdown } from './components/ProjectFilters/FilterDropdown';
import { reportTypesList } from '../../consts/reportTypes';
import { USER_ROLES } from '../../consts/userRoles';
import { CreateProject } from './components/CreateProject/index';
import { DuplicateProject } from './components/DuplicateProject/index';
import { MergeAIFM } from './components/MergeAIFM/index';
import { NewDeleteConfirmModal } from '../../components/NewDeleteConfirmModal/index';
import agentStore from '../../store/agent/index';
import { WEB_PAGE_STATE_ENUMS } from '../../components/ChatWidget/constants';
import NoProjects from './components/NoProjects';
import { REPORT_NAMINGS, REPORT_TYPES } from '../../consts/reportTypes';
import { SortButton } from './components/SortButton/index';
import { isOxFlowLocation } from '../../utils';
import toast from 'react-hot-toast';
import RowAction from './Quarters/components/RowAction';

const useStyles = createStyles(theme => ({
  borderedBox: {
    border: `1px solid ${theme.colors.gray[1]}`,
    padding: theme.spacing.md,
    borderRadius: '4px',
    background: '#fff',
    marginTop: '12px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0
  }
}));

export function ProjectList() {
  const [isOpenAddAifs, setIsOpenAddAifs] = useState<boolean>(false);
  const navigate = useNavigate();
  const { data: allUsers } = useUserGroupsAllUsers({
    customKey: 'project_list'
  }) as { data?: any[] };

  const { classes } = useStyles();
  const { data: companies, isLoading: isLoadingCompany } = useUserCompanies({
    customKey: 'project_list'
  }) as { data?: { current_company_id?: string }; isLoading: boolean };

  const [selectedWorkflows, setSelectedWorkflows] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string }>({
    key: '',
    direction: ''
  });
  const currentCompanyId = companies?.current_company_id;
  const [opened, { toggle, close }] = useDisclosure(false);

  const [openedProjectCreate, { open: openProjectCreate, close: closeProjectCreate }] =
    useDisclosure(false);

  const [openedDuplicate, { open: openDuplicate, close: closeDuplicate }] = useDisclosure(false);

  const [searchParams, setSearchParams] = useSearchParams();

  // External callers (e.g. AppTopBar's "Add Project") deep-link to ?createProject=1
  useEffect(() => {
    if (searchParams.get('createProject') === '1') {
      openProjectCreate();
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete('createProject');
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams, openProjectCreate]);
  const urlPage = parseInt(searchParams.get('page') || '', 10);
  const storedPage = parseInt(sessionStorage.getItem('projectListPage') || '', 10);
  const page = urlPage > 0 ? urlPage : storedPage > 0 ? storedPage : 1;
  const setPage = (newPage: number) => {
    sessionStorage.setItem('projectListPage', String(newPage));
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('page', String(newPage));
        return next;
      },
      { replace: true }
    );
  };
  const pageSize = 20;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);
  const [selectedAIFMType, setSelectedAIFMType] = useState<any>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<any>(null);

  const prevCompanyIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const isInitialCompanyLoad =
      prevCompanyIdRef.current === undefined && currentCompanyId !== undefined;
    prevCompanyIdRef.current = currentCompanyId;
    if (!isInitialCompanyLoad && currentCompanyId !== undefined) {
      setPage(1);
    }
  }, [currentCompanyId, debouncedSearch, selectedAIFMType, selectedAssignee, sortConfig.direction]);

  const { mutate, isLoading: isLoadingDelete } = useDeleteWorkflow();

  const {
    workflows: projects,
    pagination,
    isLoading,
    refetch
  } = useGetWorkflows({
    params: {
      headers: {
        company_id: currentCompanyId
      }
    },
    queryParams: {
      enabled: !!currentCompanyId
    },
    page,
    pageSize,
    search: debouncedSearch,
    filterByAifmType: selectedAIFMType?.key || '',
    filterByAssignedTo: selectedAssignee?.id || '',
    sortBy: sortConfig.direction || undefined
  });

  const { data: currentUserState } = useUserCompaniesCurrentUserAdminState({
    customKey: 'project_list'
  });

  const deleteWorkflowGroup = useDeleteWorkflowGroup();

  const [project, setProject] = useState<any>();
  const [openMergeModal, setOpenMergeModal] = useState<boolean>(false);
  const [selectedReportType, setSelectedReportType] = useState<string>(REPORT_TYPES.ANNEX_IV);
  const [selectedWorkflowGroup, setSelectedWorkflowGroup] = useState<any>(null);
  const [assigneeEditingState, setAssigneeEditingState] = useState<any>({
    state: ASSIGNEE_EDITING_STATES.DEFAULT
  });

  const oxFlowLocation = isOxFlowLocation();

  const selectedWorkflowReportType =
    selectedWorkflows?.length > 0
      ? projects?.find(p => p.id === selectedWorkflows?.[0])?.report_type
      : null;

  const onRenderTableRow = (item: any) => {
    if (selectedWorkflowReportType && item.report_type !== selectedWorkflowReportType) {
      return {
        style: { opacity: 0.3, cursor: 'not-allowed', pointerEvents: 'none' as const }
      };
    }
    return {};
  };

  const aifmTypeOptions = reportTypesList.map(t => ({ id: t.key, key: t.key, name: t.title }));

  const assigneeOptions = useMemo(() => {
    if (!allUsers) return [];
    return allUsers.map((u: any) => ({ id: u.azure_object_id, name: u.name }));
  }, [allUsers]);

  const isDataReady = !isLoading && !isLoadingCompany;
  const hasNoResults = projects?.length === 0;
  const hasActiveFilters = !!debouncedSearch?.trim() || !!selectedAIFMType || !!selectedAssignee;
  const shouldShowEmptyState = isDataReady && hasNoResults && !hasActiveFilters;
  const shouldShowNoSearchResults = isDataReady && hasNoResults && hasActiveFilters;

  useEffect(() => {
    if (!isDataReady || projects?.length > 0 || page <= 1) return;

    const totalPages = pagination?.pages;
    if (typeof totalPages === 'number' && totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    } else if (!totalPages || totalPages === 0) {
      setPage(1);
    }
  }, [isDataReady, projects?.length, page, pagination?.pages]);

  const onAction = (_type?: string, item?: any) => {
    setProject(item);
  };

  const onConfirmDeleteWorkflowGroup = () => {
    deleteWorkflowGroup.mutate(selectedWorkflowGroup.id, {
      onSuccess: () => {
        setSelectedWorkflowGroup(null);
        refetch();
      },
      onError: (err: any) => {
        toast.error(`Delete project group error ${err?.data?.detail}`);
      }
    });
  };

  const onConfirmDelete = (successFn: () => void) => {
    mutate(project.id, {
      onSuccess: () => {
        onAction();
        close();
        refetch();
        successFn();
      },
      onError: (err: any) => {
        toast.error(`Delete project error ${err?.data?.detail}`);
      }
    });
  };

  const toggleMergeModal = () => {
    setOpenMergeModal(prev => !prev);
  };

  const onClearGroup = () => {
    setSelectedWorkflows([]);
  };

  const onSelectWorkflow = (id: string) => {
    if (selectedWorkflows.includes(id)) {
      setSelectedWorkflows(prev => prev.filter(value => value !== id));
    } else {
      setSelectedWorkflows(prev => [...prev, id]);
    }
  };

  const onNavigateToWorkflow = useCallback(
    (id: string) => {
      const project = projects.find((item: any) => item.id === id);
      onRowClick(project);
    },
    [projects]
  );

  const handleDuplicateClick = (e: React.MouseEvent | undefined, data: any) => {
    e?.stopPropagation();
    setProject(data);
    openDuplicate();
  };

  const handleDeleteClick = (e: React.MouseEvent | undefined, data: any) => {
    e?.stopPropagation();
    onAction('delete', data);
    toggle();
  };

  const _columns = useMemo(() => {
    const _res = [
      {
        title: 'Project Name',
        key: 'name',
        renderHeadCell: () => (
          <Flex align="center" gap="4px">
            <Text fz="14px" lh="20px" c="#495057" fw="600">
              Project Name
            </Text>
            <SortButton columnName="name" setSortConfig={setSortConfig} sortConfig={sortConfig} />
          </Flex>
        ),
        render: (data: any) => {
          return (
            <Box>
              <Flex align="center">
                {!data?.is_workflow_group && (
                  <Checkbox
                    onChange={() => onSelectWorkflow(data.id)}
                    checked={selectedWorkflows.includes(data.id)}
                    size="xs"
                    onClick={e => e.stopPropagation()}
                  />
                )}
                <Text
                  style={{
                    cursor: 'pointer'
                  }}
                  truncate
                  px="10px"
                  maw="550px"
                  fz="14px"
                  lh="20px"
                  fw={400}
                  c="#000"
                >
                  {data?.name}
                </Text>
                {data?.is_workflow_group && (
                  <Flex
                    justify="center"
                    align="center"
                    fz="12px"
                    lh="17px"
                    fw={600}
                    c="#fff"
                    bg="blue.6"
                    sx={() => ({
                      minWidth: '16px',
                      height: '16px',
                      borderRadius: '50%'
                    })}
                  >
                    {data?.workflows_count}
                  </Flex>
                )}
              </Flex>
              {!data?.is_workflow_group && data?.cloned_from_workflow_id && (
                <HoverCard
                  withArrow
                  width={206}
                  shadow="md"
                  position="bottom-start"
                  offset={5}
                  key={data.cloned_from_workflow_id}
                >
                  <HoverCard.Target>
                    <UnstyledButton sx={{ cursor: 'initial', marginLeft: '26px' }}>
                      <Flex align="center" gap="4px">
                        <Text td="underline" c="#ADB5BD" fz="12px" lh="18px">
                          Duplicated
                        </Text>
                        <TbCopy size={16} color="#ADB5BD" />
                      </Flex>
                    </UnstyledButton>
                  </HoverCard.Target>
                  <HoverCard.Dropdown bg="#868E96" p="8px">
                    <Text fz="12px" lh="17px" c="white" fw="600">
                      Duplicated from:
                    </Text>
                    <UnstyledButton
                      onClick={e => {
                        e.stopPropagation();
                        onNavigateToWorkflow(data.cloned_from_workflow_id);
                      }}
                    >
                      <Flex mt="2px" gap="4px" align="center">
                        <Text fz="12px" td="underline" c="white" lh="19px">
                          {data.cloned_from_workflow_name}
                        </Text>
                        <TbExternalLink color="#fff" size={16} />
                      </Flex>
                    </UnstyledButton>
                  </HoverCard.Dropdown>
                </HoverCard>
              )}
            </Box>
          );
        }
      },
      {
        title: 'Report Type',
        key: 'report_type',
        render: (data: any) => {
          return data?.report_type || REPORT_TYPES.ANNEX_IV;
        }
      },
      {
        title: 'Date Created',
        key: 'created_at',
        renderHeadCell: () => (
          <Flex align="center" gap="4px">
            <Text fz="14px" lh="20px" c="#495057" fw="600">
              Date Created
            </Text>
            <SortButton
              columnName="created_at"
              setSortConfig={setSortConfig}
              sortConfig={sortConfig}
            />
          </Flex>
        ),
        render: (data: any) => {
          return moment(data.created_at).format('ll');
        }
      },
      {
        title: 'Date Updated',
        key: 'modified_at',
        renderHeadCell: () => (
          <Flex align="center" gap="4px">
            <Text fz="14px" lh="20px" c="#495057" fw="600">
              Date Updated
            </Text>
            <SortButton
              columnName="updated_at"
              setSortConfig={setSortConfig}
              sortConfig={sortConfig}
            />
          </Flex>
        ),
        render: (data: any) => {
          return moment(data.modified_at).format('ll');
        }
      },
      {
        title: 'Assigned To',
        key: 'assigned_to',
        renderHeadCell: () => (
          <Flex align="center" gap="4px">
            <Text fz="14px" lh="20px" c="#495057" fw="600">
              Assigned To
            </Text>
            <SortButton
              columnName="assigned_to"
              setSortConfig={setSortConfig}
              sortConfig={sortConfig}
            />
          </Flex>
        ),
        render: (data: any) => {
          return (
            <Flex onClick={e => e.stopPropagation()} justify="space-between" align="center">
              <AssignedToColumn
                key={data.id}
                project={data}
                currentUserState={currentUserState}
                assigneeEditingState={assigneeEditingState}
                setAssigneeEditingState={setAssigneeEditingState}
              />
              <Flex align="center" gap="4">
                {!data?.is_workflow_group && (
                  <CloseButton
                    onClick={e => handleDuplicateClick(e, data)}
                    icon={<TbCopy color="#CED4DA" fontSize="16px" />}
                  />
                )}

                {currentUserState?.role === USER_ROLES.ADMIN && (
                  <CloseButton
                    onClick={e =>
                      data?.is_workflow_group
                        ? setSelectedWorkflowGroup(data)
                        : handleDeleteClick(e, data)
                    }
                    icon={<TbTrash color="#CED4DA" fontSize="16px" />}
                  />
                )}
              </Flex>
            </Flex>
          );
        }
      }
    ];

    // if (currentUserState?.role === USER_ROLES.ADMIN) {
    //   _res.push({
    //     title: 'Actions',
    //     key: 'Actions',
    //     thStyle: {
    //       width: 80
    //     },
    //     render: data => {
    //       const isGroupedWorkflow = data.is_workflow_group;

    //       if (isGroupedWorkflow && data.workflows_count === 0)
    //         return (
    //           <Flex justify="center">
    //             <RowAction onDelete={() => handleDeleteClick(null, data)} />
    //           </Flex>
    //         );
    //       else if (!isGroupedWorkflow)
    //         return (
    //           <Flex justify="center">
    //             <RowAction onDelete={() => handleDeleteClick(null, data)} />
    //           </Flex>
    //         );

    //       return <></>;
    //     }
    //   });
    // }

    return _res;
  }, [currentUserState, assigneeEditingState, selectedWorkflows, onNavigateToWorkflow, sortConfig]);

  const onRowClick = async (data: any) => {
    try {
      const link = checkToRedirect(data);
      navigate(link);

      if (data?.id) {
        (authStore as any).setWorkflow(data.id, data.name);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const onOpenAddAifs = () => {
    setIsOpenAddAifs(true);
  };

  useEffect(() => {
    // Reset workflow when user navigates to project list
    (authStore as any).setWorkflow(null, '');

    (agentStore as any).setCurrentScreen(WEB_PAGE_STATE_ENUMS.Workflows);
  }, []);

  return (
    <Box
      h="calc(100vh - 68px)"
      bg="gray.0"
      pt="16"
      px="16"
      pb="3"
      sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <PageHeader title="Projects" />
      <Box className={classes.borderedBox}>
        <Flex justify="space-between" align="center">
          <Flex gap={16} align="center">
            <Button
              size="sm"
              variant="outline"
              leftSection={<TbPlus fontSize="16px" />}
              onClick={openProjectCreate}
            >
              Create New Project
            </Button>
            <TextInput
              placeholder="Search"
              leftSection={<TbSearch size={16} color="#ADB5BD" />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              size="sm"
              w={256}
            />
            <FilterDropdown
              label="Assigned to"
              placeholder="All Users"
              options={assigneeOptions}
              value={selectedAssignee}
              onChange={setSelectedAssignee}
              leftSection={<TbUser size={16} />}
            />
            {!oxFlowLocation && (
              <FilterDropdown
                label="Report Type"
                placeholder="All Types"
                options={aifmTypeOptions}
                value={selectedAIFMType}
                onChange={setSelectedAIFMType}
              />
            )}
          </Flex>
          <Button
            onClick={toggleMergeModal}
            leftSection={<TbSitemap />}
            size="sm"
            variant="outlined"
            disabled={selectedWorkflows.length === 0}
            className="consolidate-btn"
          >
            Consolidate Into Single Report{' '}
            {selectedWorkflows.length > 0 ? `(${selectedWorkflows.length})` : ''}
          </Button>
        </Flex>
        <Box
          mt="16"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0
          }}
        >
          {shouldShowEmptyState && <NoProjects />}

          {shouldShowNoSearchResults && (
            <Flex direction="column" align="center" gap="16" pt={72}>
              <TbListSearch size={24} color="#ADB5BD" />
              <Text fz="14px" c="#ADB5BD" ff="roboto">
                {"We couldn't find any projects matching your search or filters"}
              </Text>
            </Flex>
          )}

          {(projects?.length > 0 || isLoading || isLoadingCompany) && (
            <Flex direction="column" sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Table
                  trClassName="project-table-tr"
                  onRowClick={onRowClick}
                  hasIndex={false}
                  isLoading={isLoading || isLoadingCompany}
                  columns={
                    oxFlowLocation ? _columns.filter(el => el.key !== 'report_type') : _columns
                  }
                  trProps={onRenderTableRow}
                  data={projects}
                  height="100%"
                />
              </Box>

              {!isLoading && !isLoadingCompany && pagination?.pages > 1 && (
                <Flex mt="16px" sx={{ flexShrink: 0 }}>
                  <Pagination
                    value={page}
                    onChange={setPage}
                    total={pagination.pages}
                    size="sm"
                    withControls={false}
                    withEdges={false}
                  />
                </Flex>
              )}
            </Flex>
          )}
        </Box>
      </Box>

      <Drawer
        opened={openedProjectCreate}
        onClose={closeProjectCreate}
        position="right"
        styles={{
          content: {
            minWidth: 640
          }
        }}
        closeOnEscape={false}
        closeOnClickOutside={false}
        title={
          isOpenAddAifs ? (
            <Flex align="center" gap="8px">
              <UnstyledButton onClick={() => setIsOpenAddAifs(false)}>
                <IoIosArrowRoundBack fontSize="20px" />
              </UnstyledButton>
              <Text fz="16px" lh="22px" c="blue.6">
                Create New Project
              </Text>
              /
              <Text fz="16px" lh="22px" c="dark.6">
                Add {REPORT_NAMINGS.FUND[selectedReportType]}
              </Text>
            </Flex>
          ) : (
            'Create New Report'
          )
        }
      >
        <CreateProject
          isOpenAddAifs={isOpenAddAifs}
          onOpenAddAifs={onOpenAddAifs}
          refetch={refetch}
          onClose={closeProjectCreate}
          selectedReportType={selectedReportType}
          setSelectedReportType={setSelectedReportType}
        />
      </Drawer>

      <Drawer
        opened={openedDuplicate}
        onClose={closeDuplicate}
        position="right"
        title="Duplicate Project"
      >
        <DuplicateProject project={project} refetch={refetch} onClose={closeDuplicate} />
      </Drawer>

      <MergeAIFM
        isOpen={openMergeModal}
        workflowIds={selectedWorkflows}
        onClose={toggleMergeModal}
        onClearGroup={onClearGroup}
        refetch={refetch}
      />

      <NewDeleteConfirmModal
        title="Delete Project"
        description={`Are you sure you want to permanently delete ${project?.name}?`}
        isOpen={opened}
        onClose={close}
        submitLabel="Yes, Delete Project"
        cancelLabel="Cancel"
        type="delete"
        onConfirm={onConfirmDelete}
        isLoading={isLoadingDelete}
      />

      <NewDeleteConfirmModal
        title="Delete Project Group"
        description={`Are you sure you want to permanently delete ${selectedWorkflowGroup?.name}?`}
        isOpen={selectedWorkflowGroup !== null}
        onClose={() => setSelectedWorkflowGroup(null)}
        submitLabel="Yes, Delete Project Group"
        cancelLabel="Cancel"
        type="delete"
        onConfirm={onConfirmDeleteWorkflowGroup}
        isLoading={deleteWorkflowGroup.isLoading}
      />
    </Box>
  );
}
